import type { ToastContentProps } from "react-toastify/unstyled";

import { cva } from "class-variance-authority";
import { XIcon } from "lucide-react";
import { toast as reactToastify } from "react-toastify/unstyled";

import { cn } from "~/pld/lib/cn";
import FeaturedIcon from "~/pld/ui/featured-icon";

export enum ToastType {
  SUCCESS = "success",
  WARN = "warn",
  ERROR = "error",
  INFO = "info",
}

interface ToastProps extends ToastContentProps {
  content: string;
  variant: ToastType;
}

export const toastVariants = cva(
  "rounded-sm! border! px-4! py-[10px]! shadow-md!",
  {
    variants: {
      variant: {
        [ToastType.SUCCESS]:
          "border-success-light! bg-success-extra-light! text-success!",
        [ToastType.WARN]:
          "border-warning-light! bg-warning-extra-light! text-warning!",
        [ToastType.ERROR]:
          "border-error-light! bg-error-extra-light! text-error!",
        [ToastType.INFO]: "text-primary! border-gray-300! bg-gray-100!",
      },
    },
  },
);

const iconConfig = {
  [ToastType.SUCCESS]: {
    icon: "check" as const,
    iconVariant: "success" as const,
    iconClassName: "bg-success-middle-light!",
  },
  [ToastType.WARN]: {
    icon: "triangle-alert" as const,
    iconVariant: "warning" as const,
    iconClassName: "bg-warning-middle-light!",
  },
  [ToastType.ERROR]: {
    icon: "alert-circle" as const,
    iconVariant: "error" as const,
    iconClassName: "bg-error-middle-light!",
  },
  [ToastType.INFO]: {
    icon: "info" as const,
    iconVariant: "light" as const,
    iconClassName: "",
  },
} satisfies Record<
  ToastType,
  { icon: string; iconVariant: string; iconClassName: string }
>;

export function Toast({ content, closeToast, variant }: ToastProps) {
  const { icon, iconClassName, iconVariant } = iconConfig[variant];
  return (
    <div className="flex w-full items-center gap-4">
      <FeaturedIcon
        icon={icon}
        variant={iconVariant}
        size="md"
        className={cn("shadow-sm", iconClassName)}
      />
      <p className="grow body-md-semibold whitespace-pre-line">{content}</p>
      <button
        type="button"
        onClick={closeToast}
        aria-label="Close toast"
        className="shrink-0"
      >
        <XIcon className="size-5" />
      </button>
    </div>
  );
}

function success(content: string) {
  reactToastify(
    (toastProps) => (
      <Toast content={content} variant={ToastType.SUCCESS} {...toastProps} />
    ),
    {
      className: toastVariants({ variant: ToastType.SUCCESS }),
      hideProgressBar: true,
      closeButton: false,
    },
  );
}

function warn(content: string) {
  reactToastify(
    (toastProps) => (
      <Toast content={content} variant={ToastType.WARN} {...toastProps} />
    ),
    {
      className: toastVariants({ variant: ToastType.WARN }),
      hideProgressBar: true,
      closeButton: false,
    },
  );
}

function error(content: string) {
  reactToastify(
    (toastProps) => (
      <Toast content={content} variant={ToastType.ERROR} {...toastProps} />
    ),
    {
      className: toastVariants({ variant: ToastType.ERROR }),
      hideProgressBar: true,
      closeButton: false,
    },
  );
}

function info(content: string) {
  reactToastify(
    (toastProps) => (
      <Toast content={content} variant={ToastType.INFO} {...toastProps} />
    ),
    {
      className: toastVariants({ variant: ToastType.INFO }),
      hideProgressBar: true,
      closeButton: false,
    },
  );
}

export const toast = {
  success: (content: string) => success(content),
  warn: (content: string) => warn(content),
  error: (content: string) => error(content),
  info: (content: string) => info(content),
};
